import { Typography, Container, Button, Box, Grid, Card, CardContent } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';

const walmartFeatures = [
	{
		icon: <CloudUploadIcon fontSize="large" />,
		title: "CSV Upload",
		text: "Easily upload CSV files to add items to Walmart inventory.",
		color: '#0066cc'
	},
	{
		icon: <InventoryIcon fontSize="large" />,
		title: "Batch Processing",
		text: "Add multiple items at once with minimal manual input.",
		color: '#8e44ad'
	},
	{
		icon: <LocalShippingIcon fontSize="large" />,
		title: "WFS Shipments",
		text: "Create and manage shipments to Walmart Fulfillment Services.",
		color: '#e74c3c'
	},
];

const WalmartPage = () => {
	const { theme } = useTheme();
	const { isAuth, user, userSU } = useAuth();
	const navigate = useNavigate();

	const styles = {
		button: {
			textTransform: 'none',
			fontFamily: theme.fontFamily,
			color: '#ffffff',
			borderRadius: '25px',
			background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
			border: 'none',
			mt: 3,
			mb: 2,
			px: "50px",
			py: "15px",
			fontSize: "120%",
			fontWeight: 600,
			boxShadow: '0 8px 25px rgba(0, 102, 204, 0.3)',
			transition: 'all 0.3s ease',
			'&:hover': {
				background: 'linear-gradient(135deg, #004499 0%, #003366 100%)',
				transform: 'translateY(-2px)',
				boxShadow: '0 12px 35px rgba(0, 102, 204, 0.4)',
			}
		},
		heroSection: {
			minHeight: '85vh',
			backgroundColor: theme.background.primary,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			position: 'relative',
			overflow: 'hidden',
			py: 6,
			background: `linear-gradient(135deg, ${theme.background.primary} 0%, ${theme.background.homeSection} 100%)`
		},
		featuresSection: {
			minHeight: '50vh',
			padding: '5rem 2rem',
			backgroundColor: theme.background.homeSection,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			position: 'relative'
		},
		featureCard: {
			height: '100%',
			borderRadius: '20px',
			boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
			transition: 'all 0.3s ease',
			border: `1px solid ${theme.border.light}`,
			background: `linear-gradient(135deg, ${theme.background.primary} 0%, rgba(255,255,255,0.9) 100%)`,
			'&:hover': {
				transform: 'translateY(-8px)',
				boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
			}
		},
		walmartGraphic: {
			width: '350px',
			height: '350px',
			background: 'linear-gradient(135deg, #0066cc 0%, #004499 30%, #ffc220 70%, #ffaa00 100%)',
			borderRadius: '30px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			boxShadow: '0 20px 60px rgba(0, 102, 204, 0.3)',
			position: 'relative',
			overflow: 'hidden',
			'&::before': {
				content: '""',
				position: 'absolute',
				top: '20px',
				right: '20px',
				width: '60px',
				height: '60px',
				background: 'rgba(255, 255, 255, 0.2)',
				borderRadius: '50%',
			}
		}
	};

	if (!!userSU && !user) {
		return <Navigate to="/super-user" replace />;
	}

	return (
		<>
			{/* Hero Section */}
			<Box sx={styles.heroSection}>
				<Container sx={{ position: 'relative', zIndex: 1 }}>
					<Grid container spacing={6} alignItems="center">
						{/* Text Content */}
						<Grid item xs={12} md={6}>
							<Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
								<Typography 
									variant="h1" 
									sx={{ 
										color: theme.color.primary, 
										fontFamily: theme.fontFamily,
										fontSize: { xs: '2.2rem', md: '3.2rem' },
										fontWeight: 800,
										mb: 3,
										lineHeight: 1.1,
										background: `linear-gradient(135deg, ${theme.color.primary} 0%, #004499 100%)`,
										WebkitBackgroundClip: 'text',
										WebkitTextFillColor: 'transparent',
										backgroundClip: 'text'
									}}
								>
									Grow on Walmart with Inventory Falcon
								</Typography>
								<Typography 
									variant="body1" 
									sx={{ 
										color: theme.text.secondary, 
										fontFamily: theme.fontFamily,
										fontSize: '1.2rem',
										mb: 4,
										lineHeight: 1.7,
										fontWeight: 400
									}}
								>
									We provide everything you need to dominate one of the fastest-growing e-commerce platforms.
                                    From intelligent listing optimization to automated inventory management.
								</Typography>
								
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
									<RocketLaunchIcon sx={{ color: theme.color.primary, fontSize: '2rem' }} />
									<Typography sx={{ 
										color: theme.text.primary, 
										fontWeight: 600,
										fontSize: '1.1rem'
									}}>
										Launch your success story today
									</Typography>
								</Box>
								
								<Button 
									variant="contained" 
									sx={styles.button} 
									onClick={() => navigate(isAuth ? '/' : '/login')}
								>
									Get Started Today
								</Button>
							</Box>
						</Grid>
						
						{/* Graphic/Image */}
						<Grid item xs={12} md={6}>
							<Box sx={{ 
								display: 'flex', 
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '400px'
							}}>
								<img src="/assets/walmart-logo.svg" style={{width: "80%"}}/>
							</Box>
						</Grid>
					</Grid>
				</Container>
			</Box>

			{/* Tagline Section */}
			<Box sx={{ 
				textAlign: 'center', 
				py: 5, 
				backgroundColor: theme.background.primary,
				borderTop: `1px solid ${theme.border.light}`,
				position: 'relative'
			}}>
				<Container>
					<Typography 
						variant="h3" 
						sx={{ 
							color: theme.color.primary, 
							fontFamily: theme.fontFamily,
							fontWeight: 700,
							mb: 2,
							fontSize: { xs: '2rem', md: '2.5rem' }
						}}
					>
						Your Complete Walmart Success Platform
					</Typography>
					<Typography 
						variant="h6" 
						sx={{ 
							color: theme.text.secondary, 
							fontFamily: theme.fontFamily,
							fontWeight: 500,
							fontSize: '1.3rem'
						}}
					>
						From Listing to Reports!
					</Typography>
				</Container>
			</Box>

			{/* Features Section */}
			<Box sx={styles.featuresSection}>
				<Container>
					<Typography 
						variant="h4" 
						sx={{ 
							textAlign: 'center',
							color: theme.text.primary,
							fontFamily: theme.fontFamily,
							fontWeight: 700,
							mb: 6,
							fontSize: { xs: '1.8rem', md: '2.2rem' }
						}}
					>
						Powerful Tools for Every Need
					</Typography>
					
					<Grid container spacing={4} sx={{ maxWidth: '1400px', mx: 'auto' }}>
						{walmartFeatures.map((feature, index) => (
							<Grid item xs={12} sm={6} md={4} key={index}>
								<Card sx={styles.featureCard}>
									<CardContent sx={{
										textAlign: 'center',
										p: 4,
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center'
									}}>
										<Box sx={{ 
											color: feature.color, 
											mb: 3,
											p: 2,
											borderRadius: '50%',
											backgroundColor: `${feature.color}15`,
											'& svg': {
												fontSize: '3.5rem'
											}
										}}>
											{feature.icon}
										</Box>
										<Typography 
											variant="h6" 
											sx={{ 
												color: theme.text.primary, 
												fontFamily: theme.fontFamily,
												fontWeight: 700,
												mb: 2,
												fontSize: '1.3rem'
											}}
										>
											{feature.title}
										</Typography>
										<Typography 
											variant="body1" 
											sx={{ 
												color: theme.text.secondary, 
												fontFamily: theme.fontFamily,
												lineHeight: 1.6,
												fontSize: '1rem'
											}}
										>
											{feature.text}
										</Typography>
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
				</Container>
			</Box>

			{/* Call to Action Section */}
			<Box sx={{
				py: 8,
				background: `linear-gradient(135deg, ${theme.color.primary} 0%, #004499 100%)`,
				textAlign: 'center',
				color: '#ffffff',
				position: 'relative',
				overflow: 'hidden',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
				}
			}}>
				<Container sx={{ position: 'relative', zIndex: 1 }}>
					<Typography 
						variant="h3" 
						sx={{ 
							fontFamily: theme.fontFamily,
							fontWeight: 800,
							mb: 3,
							fontSize: { xs: '2rem', md: '2.8rem' }
						}}
					>
						Ready to Transform Your Walmart Business?
					</Typography>
					<Typography 
						variant="h6" 
						sx={{ 
							fontFamily: theme.fontFamily,
							mb: 4,
							opacity: 0.9,
							fontSize: '1.2rem',
							maxWidth: '600px',
							mx: 'auto'
						}}
					>
						Join successful sellers who've scaled their business with Inventory Falcon
					</Typography>
					<Button 
						variant="contained" 
						sx={{
							...styles.button,
							background: '#ffffff',
							color: theme.color.primary,
							fontSize: '1.3rem',
							px: '60px',
							py: '18px',
							'&:hover': {
								background: '#f8f9fa',
								transform: 'translateY(-3px)',
								boxShadow: '0 15px 40px rgba(255,255,255,0.3)',
							}
						}}
						onClick={() => navigate(isAuth ? '/' : '/login')}
					>
						Start Your Success Story
					</Button>
				</Container>
			</Box>
		</>
	);
};

export default WalmartPage;